/**
 * Keeps a set of horizontal scroll nodes on the same {@link ScrollSyncNode.scrollLeft}.
 * Import metadata strips register here so header, rows, and the bottom rail stay aligned.
 */

export type ScrollSyncNode = {
  isConnected: boolean;
  scrollLeft: number;
};

export type ScrollSyncRegistry<T extends ScrollSyncNode = ScrollSyncNode> = {
  register: (node: T | null) => T | null;
  applyScrollLeft: (source: T, scrollLeft: number) => void;
  clear: () => void;
};

const connectedLeader = <T extends ScrollSyncNode>(nodes: Set<T>): T | null =>
  [...nodes].find((node) => node.isConnected) ?? null;

/**
 * Fan-out registry for sibling scroll containers.
 * `register` returns the first connected node (rail target).
 */
export const createScrollSyncRegistry = <
  T extends ScrollSyncNode = ScrollSyncNode,
>(): ScrollSyncRegistry<T> => {
  const nodes = new Set<T>();
  let lastScrollLeft = 0;

  return {
    register: (node) => {
      if (node) {
        nodes.add(node);
        node.scrollLeft = lastScrollLeft;
      } else {
        nodes.forEach((n) => {
          if (!n.isConnected) nodes.delete(n);
        });
      }
      return connectedLeader(nodes);
    },
    applyScrollLeft: (source, scrollLeft) => {
      lastScrollLeft = scrollLeft;
      nodes.forEach((n) => {
        if (n !== source) n.scrollLeft = scrollLeft;
      });
    },
    clear: () => {
      nodes.clear();
      lastScrollLeft = 0;
    },
  };
};
